import { useImperativeHandle, useMemo, useRef } from 'react';
import { type AnyFieldApi, useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { Button, DatePickerField, InputNumberField } from '@nvision/ui';

import {
  BranchField,
  CargoTypeField,
  CarrierField,
  CarrierServiceField,
  CurrencyField,
  EquipmentTypeField,
  PackageTypeField,
  PricingTypeField,
  TariffTypeField,
  TransportationModeField,
} from '@/modules/entities';
import { getEffectiveDateBounds } from '@/utils/date';

import { getDefaultPricingsFormValues } from '../../../hooks';
import { type PricingsFilters, pricingsFiltersSchema } from '../../../models';

import styles from './pricings-search-filters-form.module.css';

export type PricingsSearchFiltersFormRef = {
  submit: () => void;
  canSubmit: () => boolean;
  reset: (values: PricingsFilters) => void;
};

interface PricingsSearchFiltersProps {
  customerKey?: number;
  formRef: React.RefObject<PricingsSearchFiltersFormRef>;
  initialValues: PricingsFilters;
  onSubmit: (values: PricingsFilters, meta: { customerKey: number }) => void;
  onReset?: () => void;
  variant?: 'modal' | 'sidebar';
}

export const PricingsSearchFiltersForm: React.FC<PricingsSearchFiltersProps> = ({
  customerKey,
  formRef,
  initialValues,
  onReset,
  onSubmit,
  variant = 'sidebar',
}) => {
  const defaultValuesRef = useRef(initialValues);
  const form = useForm({
    defaultValues: defaultValuesRef.current,
    onSubmit: async (props) => {
      if (props.value && customerKey) {
        onSubmit(props.value, { customerKey });
      }
    },
    validators: {
      onChange: pricingsFiltersSchema,
      onSubmit: pricingsFiltersSchema,
    },
  });

  const resetForm = (values: PricingsFilters) => {
    defaultValuesRef.current = values;
    form.reset(values, { keepDefaultValues: false });
  };

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: resetForm,
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.validate('submit');
    form.handleSubmit();
  };

  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const handleReset = () => {
    resetForm(getDefaultPricingsFormValues(timeZone));
    onReset?.();
  };

  const resetPackingCode = () => {
    form.setFieldValue('packingCode', null);
  };

  const values = useSelector(form.store, (state) => state.values);
  const selectedCarrierKey = values.carrier?.id != null ? Number(values.carrier.id) : undefined;
  const selectedCargoTypeId = values.cargoType?.id != null ? String(values.cargoType.id) : undefined;

  const { effectiveFromMaxValue, effectiveToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.effectiveFrom, values.effectiveTo, timeZone),
    [timeZone, values.effectiveFrom, values.effectiveTo],
  );

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  const isModal = variant === 'modal';

  return (
    <form onSubmit={handleSubmit}>
      <div className={isModal ? styles.fields : undefined}>
        <form.Field name="carrier">
          {(field) => (
            <CarrierField
              value={field.state.value ?? null}
              clearable
              defaultFilters={{ customerKey: customerKey ?? 0 }}
              onSelectionChange={(carrier) => {
                field.handleChange(carrier ?? null);
                resetPackingCode();
              }}
              label="Transportation Provider"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="service">
          {(field) => (
            <CarrierServiceField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(carrierService) => field.handleChange(carrierService ?? null)}
              label="Service"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="pricingKey">
          {(field) => (
            <InputNumberField
              value={field.state.value ?? ''}
              clearable
              min={0}
              step={1}
              onChange={(e) => field.handleChange(e.target.value || null)}
              label="Pricing Key"
              placeholder="Enter"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="effectiveFrom">
          {(field) => (
            <DatePickerField
              value={field.state.value}
              maxValue={effectiveFromMaxValue}
              onChange={(date) => field.handleChange(date ?? '')}
              label="Effective From"
              size="sm"
              timeZone={timeZone}
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="effectiveTo">
          {(field) => (
            <DatePickerField
              value={field.state.value}
              minValue={effectiveToMinValue}
              includeEndOfDay
              onChange={(date) => field.handleChange(date ?? undefined)}
              label="Effective To"
              size="sm"
              timeZone={timeZone}
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="branch">
          {(field) => (
            <BranchField
              value={field.state.value ?? null}
              clearable
              defaultFilters={{ customerKey: customerKey ?? 0 }}
              onSelectionChange={(branch) => field.handleChange(branch ?? null)}
              label="Branch"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="cargoType">
          {(field) => (
            <CargoTypeField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(cargoType) => {
                field.handleChange(cargoType ?? null);
                resetPackingCode();
              }}
              label="Cargo Type"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="pricingBasis">
          {(field) => (
            <TariffTypeField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(pricingBasis) => field.handleChange(pricingBasis ?? null)}
              label="Pricing Basis"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="transportationMode">
          {(field) => (
            <TransportationModeField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(transportationMode) => field.handleChange(transportationMode ?? null)}
              label="Mode"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="pricingType">
          {(field) => (
            <PricingTypeField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(pricingType) => field.handleChange(pricingType ?? null)}
              label="Pricing Type"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="currency">
          {(field) => (
            <CurrencyField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(currency) => field.handleChange(currency ?? null)}
              label="Currency"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="equipmentType">
          {(field) => (
            <EquipmentTypeField
              value={field.state.value ?? null}
              clearable
              onSelectionChange={(equipmentType) => field.handleChange(equipmentType ?? null)}
              label="Equipment Type"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>

        <form.Field name="packingCode">
          {(field) => (
            <PackageTypeField
              value={field.state.value ?? null}
              clearable
              defaultFilters={{ cargoTypeId: selectedCargoTypeId, carrierKey: selectedCarrierKey }}
              onSelectionChange={(packageType) => field.handleChange(packageType ?? null)}
              label="Package Type"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
            />
          )}
        </form.Field>
        {isModal && (
          <div className={styles.actions}>
            <Button type="submit">Search</Button>
            <Button type="button" outline secondary onClick={handleReset}>
              Reset
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};
